import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Constants from Coordinator types
const CRYPTO_ASSETS = ['USDT', 'USDC', 'DAI', 'BTC', 'ETH', 'BNB', 'SOL', 'MRX', 'TRX', 'MATIC', 'ARB'] as const;
const NETWORKS = ['ERC20', 'TRC20', 'BEP20', 'SOL', 'POLYGON', 'ARB', 'OP', 'BASE', 'AVAX', 'NATIVE'] as const;
const FIAT_CURRENCIES = ['VND', 'USD', 'EUR', 'GBP', 'SGD', 'THB', 'MYR', 'PHP'] as const;
const PAYMENT_METHODS = [
  'bank_transfer', 'momo', 'zalopay', 'viettelpay', 'vnpay',
  'wise', 'revolut', 'paypal', 'skrill', 'payoneer', 'pingpong', 'cash'
] as const;

// Network mapping for each crypto
const CRYPTO_NETWORKS: Record<string, readonly string[]> = {
  USDT: ['ERC20', 'TRC20', 'BEP20', 'SOL', 'POLYGON', 'ARB', 'OP'],
  USDC: ['ERC20', 'TRC20', 'BEP20', 'SOL', 'POLYGON', 'ARB', 'OP', 'BASE'],
  DAI: ['ERC20', 'BEP20', 'POLYGON', 'ARB', 'OP'],
  BTC: ['NATIVE'],
  ETH: ['NATIVE', 'ARB', 'OP', 'BASE'],
  BNB: ['BEP20', 'NATIVE'],
  SOL: ['SOL'],
  MRX: ['ERC20', 'BEP20'],
  TRX: ['NATIVE'],
  MATIC: ['POLYGON', 'ERC20'],
  ARB: ['ARB'],
};

// Payment method display info
const PAYMENT_INFO: Record<string, { name: string; icon: string; currencies: string[] }> = {
  bank_transfer: { name: 'Bank Transfer', icon: '🏦', currencies: ['VND', 'USD', 'EUR', 'GBP', 'SGD', 'THB', 'MYR', 'PHP'] },
  momo: { name: 'MoMo', icon: '💜', currencies: ['VND'] },
  zalopay: { name: 'ZaloPay', icon: '💙', currencies: ['VND'] },
  viettelpay: { name: 'ViettelPay', icon: '❤️', currencies: ['VND'] },
  vnpay: { name: 'VNPay', icon: '💚', currencies: ['VND'] },
  wise: { name: 'Wise', icon: '🌍', currencies: ['USD', 'EUR', 'GBP', 'SGD', 'THB', 'MYR', 'PHP'] },
  revolut: { name: 'Revolut', icon: '💳', currencies: ['USD', 'EUR', 'GBP', 'SGD'] },
  paypal: { name: 'PayPal', icon: '🅿️', currencies: ['USD', 'EUR', 'GBP', 'SGD', 'THB', 'MYR', 'PHP'] },
  skrill: { name: 'Skrill', icon: '💰', currencies: ['USD', 'EUR', 'GBP'] },
  payoneer: { name: 'Payoneer', icon: '💵', currencies: ['USD', 'EUR', 'GBP'] },
  pingpong: { name: 'PingPong', icon: '🏓', currencies: ['USD', 'EUR', 'GBP'] },
  cash: { name: 'Cash (F2F)', icon: '💸', currencies: ['VND', 'USD'] },
};

// Crypto display info
const CRYPTO_INFO: Record<string, { icon: string; name: string }> = {
  USDT: { icon: '💵', name: 'Tether USD' },
  USDC: { icon: '💲', name: 'USD Coin' },
  DAI: { icon: '🔶', name: 'Dai' },
  BTC: { icon: '₿', name: 'Bitcoin' },
  ETH: { icon: 'Ξ', name: 'Ethereum' },
  BNB: { icon: '🟡', name: 'BNB' },
  SOL: { icon: '◎', name: 'Solana' },
  MRX: { icon: '🔷', name: 'Metrix' },
  TRX: { icon: '🔺', name: 'TRON' },
  MATIC: { icon: '🟣', name: 'Polygon' },
  ARB: { icon: '🔵', name: 'Arbitrum' },
};

// Fiat display info
const FIAT_INFO: Record<string, { icon: string; symbol: string }> = {
  VND: { icon: '🇻🇳', symbol: '₫' },
  USD: { icon: '🇺🇸', symbol: '$' },
  EUR: { icon: '🇪🇺', symbol: '€' },
  GBP: { icon: '🇬🇧', symbol: '£' },
  SGD: { icon: '🇸🇬', symbol: 'S$' },
  THB: { icon: '🇹🇭', symbol: '฿' },
  MYR: { icon: '🇲🇾', symbol: 'RM' },
  PHP: { icon: '🇵🇭', symbol: '₱' },
};

type OrderType = 'buy' | 'sell';
type Step = 'crypto' | 'network' | 'fiat' | 'amount' | 'payment' | 'confirm';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function CreateOrder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderType = (searchParams.get('type') as OrderType) || 'buy';
  
  const user = window.Telegram?.WebApp?.initDataUnsafe?.user;
  
  // Form state
  const [step, setStep] = useState<Step>('crypto');
  const [crypto, setCrypto] = useState<string>('');
  const [network, setNetwork] = useState<string>('');
  const [fiat, setFiat] = useState<string>('VND');
  const [amount, setAmount] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [selectedPayments, setSelectedPayments] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  // Available networks for selected crypto
  const availableNetworks = useMemo(() => {
    return crypto ? (CRYPTO_NETWORKS[crypto] || []) : [];
  }, [crypto]);

  // Available payment methods for selected fiat
  const availablePayments = useMemo(() => {
    return PAYMENT_METHODS.filter(pm => 
      PAYMENT_INFO[pm].currencies.includes(fiat)
    );
  }, [fiat]);

  // Calculate total
  const totalFiat = useMemo(() => {
    const a = parseFloat(amount) || 0;
    const p = parseFloat(price) || 0;
    return a * p;
  }, [amount, price]);

  // Format number with commas
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('vi-VN').format(num);
  };

  // Handle Telegram BackButton
  useEffect(() => {
    const BackButton = window.Telegram?.WebApp?.BackButton;
    if (BackButton) {
      BackButton.show();
      const handleBack = () => {
        if (step === 'crypto') {
          navigate('/');
        } else {
          const steps: Step[] = ['crypto', 'network', 'fiat', 'amount', 'payment', 'confirm'];
          const currentIndex = steps.indexOf(step);
          setStep(steps[currentIndex - 1]);
        }
      };
      BackButton.onClick(handleBack);
      return () => {
        BackButton.offClick(handleBack);
        BackButton.hide();
      };
    }
  }, [step, navigate]);

  // Handle crypto selection
  const handleSelectCrypto = (c: string) => {
    setCrypto(c);
    setNetwork('');
    // If only one network, auto-select
    const networks = CRYPTO_NETWORKS[c] || [];
    if (networks.length === 1) {
      setNetwork(networks[0]);
      setStep('fiat');
    } else {
      setStep('network');
    }
  };

  // Handle network selection
  const handleSelectNetwork = (n: string) => {
    setNetwork(n);
    setStep('fiat');
  };

  // Handle fiat selection
  const handleSelectFiat = (f: string) => {
    setFiat(f);
    setSelectedPayments([]); // Reset payments when fiat changes
    setStep('amount');
  };

  // Handle payment toggle
  const togglePayment = (pm: string) => {
    setSelectedPayments(prev => 
      prev.includes(pm) 
        ? prev.filter(p => p !== pm)
        : [...prev, pm]
    );
  };

  // Submit order
  const handleSubmit = async () => {
    if (!user) {
      setError('Vui lòng mở từ Telegram');
      return;
    }

    if (selectedPayments.length === 0) {
      setError('Vui lòng chọn ít nhất 1 phương thức thanh toán');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const orderData = {
        type: orderType,
        makerId: user.id,
        makerUsername: user.username,
        crypto,
        network,
        fiat,
        amount: parseFloat(amount),
        price: parseFloat(price),
        totalFiat,
        paymentMethods: selectedPayments,
      };

      const response = await fetch(`${API_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Không thể tạo lệnh');
      }

      const result = await response.json();
      
      // Show success and navigate
      if (result.matched) {
        // Matched! Navigate to chat
        navigate(`/chat/${result.sessionId}`);
      } else {
        // Order created, go to orders list
        navigate('/orders');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Có lỗi xảy ra');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render steps
  const renderStep = () => {
    switch (step) {
      case 'crypto':
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-center">
              Chọn Crypto để {orderType === 'buy' ? 'MUA' : 'BÁN'}
            </h2>
            <div className="grid grid-cols-3 gap-3">
              {CRYPTO_ASSETS.map(c => (
                <button
                  key={c}
                  onClick={() => handleSelectCrypto(c)}
                  className={`p-4 rounded-xl text-center transition-all ${
                    crypto === c 
                      ? 'bg-telegram-button text-white ring-2 ring-blue-400' 
                      : 'bg-telegram-secondary hover:bg-telegram-secondary/80'
                  }`}
                >
                  <div className="text-2xl mb-1">{CRYPTO_INFO[c]?.icon || '💰'}</div>
                  <div className="font-semibold">{c}</div>
                </button>
              ))}
            </div>
          </div>
        );

      case 'network':
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-center">
              Chọn Network cho {crypto}
            </h2>
            <p className="text-center text-telegram-hint text-sm">
              ⚠️ Chọn đúng network để tránh mất coin
            </p>
            <div className="grid grid-cols-2 gap-3">
              {availableNetworks.map(n => (
                <button
                  key={n}
                  onClick={() => handleSelectNetwork(n)}
                  className={`p-4 rounded-xl text-center transition-all ${
                    network === n 
                      ? 'bg-telegram-button text-white ring-2 ring-blue-400' 
                      : 'bg-telegram-secondary hover:bg-telegram-secondary/80'
                  }`}
                >
                  <div className="font-semibold">{n}</div>
                  <div className="text-xs opacity-70">
                    {n === 'ERC20' && 'Ethereum'}
                    {n === 'TRC20' && 'TRON'}
                    {n === 'BEP20' && 'BSC'}
                    {n === 'SOL' && 'Solana'}
                    {n === 'POLYGON' && 'Polygon'}
                    {n === 'ARB' && 'Arbitrum'}
                    {n === 'OP' && 'Optimism'}
                    {n === 'BASE' && 'Base'}
                    {n === 'AVAX' && 'Avalanche'}
                    {n === 'NATIVE' && 'Native Chain'}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 'fiat':
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-center">
              Chọn tiền tệ thanh toán
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {FIAT_CURRENCIES.map(f => (
                <button
                  key={f}
                  onClick={() => handleSelectFiat(f)}
                  className={`p-4 rounded-xl text-center transition-all ${
                    fiat === f 
                      ? 'bg-telegram-button text-white ring-2 ring-blue-400' 
                      : 'bg-telegram-secondary hover:bg-telegram-secondary/80'
                  }`}
                >
                  <div className="text-2xl mb-1">{FIAT_INFO[f]?.icon}</div>
                  <div className="font-semibold">{f}</div>
                  <div className="text-xs opacity-70">{FIAT_INFO[f]?.symbol}</div>
                </button>
              ))}
            </div>
          </div>
        );

      case 'amount':
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-center">
              Nhập số lượng và giá
            </h2>
            
            {/* Amount input */}
            <div className="space-y-2">
              <label className="text-sm text-telegram-hint">
                Số lượng {crypto}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full p-4 pr-20 rounded-xl bg-telegram-secondary text-lg font-mono focus:outline-none focus:ring-2 focus:ring-telegram-button"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-semibold text-telegram-hint">
                  {crypto}
                </span>
              </div>
            </div>

            {/* Price input */}
            <div className="space-y-2">
              <label className="text-sm text-telegram-hint">
                Giá 1 {crypto} ({fiat})
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={price}
                  onChange={e => setPrice(e.target.value)}
                  placeholder="0"
                  className="w-full p-4 pr-20 rounded-xl bg-telegram-secondary text-lg font-mono focus:outline-none focus:ring-2 focus:ring-telegram-button"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-semibold text-telegram-hint">
                  {fiat}
                </span>
              </div>
            </div>

            {/* Total display */}
            {totalFiat > 0 && (
              <div className="bg-telegram-secondary rounded-xl p-4 text-center">
                <div className="text-sm text-telegram-hint mb-1">Tổng tiền</div>
                <div className="text-2xl font-bold">
                  {FIAT_INFO[fiat]?.symbol}{formatNumber(totalFiat)} {fiat}
                </div>
              </div>
            )}

            {/* Continue button */}
            <button
              onClick={() => setStep('payment')}
              disabled={!amount || !price || parseFloat(amount) <= 0 || parseFloat(price) <= 0}
              className="w-full p-4 rounded-xl bg-telegram-button text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Tiếp tục →
            </button>
          </div>
        );

      case 'payment':
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-center">
              Chọn phương thức thanh toán
            </h2>
            <p className="text-center text-telegram-hint text-sm">
              Có thể chọn nhiều phương thức
            </p>
            
            <div className="space-y-2">
              {availablePayments.map(pm => (
                <button
                  key={pm}
                  onClick={() => togglePayment(pm)}
                  className={`w-full p-4 rounded-xl flex items-center gap-3 transition-all ${
                    selectedPayments.includes(pm)
                      ? 'bg-telegram-button text-white'
                      : 'bg-telegram-secondary hover:bg-telegram-secondary/80'
                  }`}
                >
                  <span className="text-xl">{PAYMENT_INFO[pm].icon}</span>
                  <span className="font-medium flex-1 text-left">{PAYMENT_INFO[pm].name}</span>
                  {selectedPayments.includes(pm) && <span>✓</span>}
                </button>
              ))}
            </div>

            {/* Continue button */}
            <button
              onClick={() => setStep('confirm')}
              disabled={selectedPayments.length === 0}
              className="w-full p-4 rounded-xl bg-telegram-button text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Xác nhận lệnh →
            </button>
          </div>
        );

      case 'confirm':
        return (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-center">
              Xác nhận lệnh {orderType === 'buy' ? 'MUA' : 'BÁN'}
            </h2>

            {/* Order summary */}
            <div className="bg-telegram-secondary rounded-xl p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-telegram-hint">Loại lệnh</span>
                <span className={`font-semibold ${orderType === 'buy' ? 'text-green-500' : 'text-blue-500'}`}>
                  {orderType === 'buy' ? '🟢 MUA' : '🔵 BÁN'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-telegram-hint">Crypto</span>
                <span className="font-semibold">{crypto} ({network})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-telegram-hint">Số lượng</span>
                <span className="font-semibold">{amount} {crypto}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-telegram-hint">Giá</span>
                <span className="font-semibold">{formatNumber(parseFloat(price))} {fiat}</span>
              </div>
              <hr className="border-telegram-hint/20" />
              <div className="flex justify-between text-lg">
                <span className="font-semibold">Tổng</span>
                <span className="font-bold text-telegram-button">
                  {FIAT_INFO[fiat]?.symbol}{formatNumber(totalFiat)} {fiat}
                </span>
              </div>
            </div>

            {/* Payment methods */}
            <div className="bg-telegram-secondary rounded-xl p-4">
              <div className="text-sm text-telegram-hint mb-2">Thanh toán qua</div>
              <div className="flex flex-wrap gap-2">
                {selectedPayments.map(pm => (
                  <span key={pm} className="bg-telegram-button/20 text-telegram-button px-3 py-1 rounded-full text-sm">
                    {PAYMENT_INFO[pm].icon} {PAYMENT_INFO[pm].name}
                  </span>
                ))}
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-500 rounded-xl p-3 text-center">
                {error}
              </div>
            )}

            {/* Submit button */}
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`w-full p-4 rounded-xl font-semibold text-white transition-all ${
                orderType === 'buy' 
                  ? 'bg-green-500 hover:bg-green-600' 
                  : 'bg-blue-500 hover:bg-blue-600'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Đang tạo...
                </span>
              ) : (
                <span>
                  ✅ Đăng lệnh {orderType === 'buy' ? 'MUA' : 'BÁN'}
                </span>
              )}
            </button>

            {/* Info */}
            <p className="text-center text-telegram-hint text-xs">
              Lệnh sẽ được đăng lên sàn và tự động khớp khi có người phù hợp
            </p>
          </div>
        );
    }
  };

  // Step indicator
  const steps: Step[] = ['crypto', 'network', 'fiat', 'amount', 'payment', 'confirm'];
  const currentStepIndex = steps.indexOf(step);

  return (
    <div className="p-4 max-w-md mx-auto">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className={`text-xl font-bold ${orderType === 'buy' ? 'text-green-500' : 'text-blue-500'}`}>
          {orderType === 'buy' ? '💰 TẠO LỆNH MUA' : '💵 TẠO LỆNH BÁN'}
        </h1>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-1 mb-6">
        {steps.map((s, i) => (
          <div
            key={s}
            className={`flex-1 h-1 rounded-full transition-all ${
              i <= currentStepIndex ? 'bg-telegram-button' : 'bg-telegram-secondary'
            }`}
          />
        ))}
      </div>

      {/* Current step content */}
      {renderStep()}
    </div>
  );
}
